import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Eye, EyeOff, ShieldCheck, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";

const MIN_LEN = 8;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [showPwd, setShowPwd] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Lien invalide ou incomplet. Demandez un nouveau lien.");
  }, [token]);

  const tooShort = password.length > 0 && password.length < MIN_LEN;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = useMemo(
    () => token && password.length >= MIN_LEN && password === confirm && !loading,
    [token, password, confirm, loading]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 2400);
    } catch (err) {
      setError(err.message || "Impossible de réinitialiser le mot de passe.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="page-reset-password"
      className="min-h-screen flex flex-col relative overflow-hidden bg-[#F7F8FA]"
    >
      <div
        aria-hidden
        className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C8FF2F]/25 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#D9FFE8]/40 blur-3xl"
      />

      <header className="relative z-10 px-6 py-8">
        <div className="max-w-md mx-auto">
          <Logo variant="full" size="md" rounded="rounded-lg" />
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md wyvio-fade-in">
          {success ? (
            <div data-testid="reset-success">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#C8FF2F] text-[#111111]">
                <ShieldCheck size={20} strokeWidth={2.25} />
              </div>
              <h1 className="mt-6 text-[36px] sm:text-[42px] leading-[1.05] font-semibold tracking-tight text-[#111111]">
                Mot de passe mis à jour
              </h1>
              <p className="mt-3 text-[15px] text-[#6B7280] leading-relaxed">
                Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
                Redirection en cours…
              </p>
              <Link
                to="/login"
                data-testid="reset-go-login"
                className="mt-8 inline-flex items-center gap-2 text-[13px] font-medium text-[#111111] hover:opacity-70 transition-opacity"
              >
                Aller à la connexion
                <ArrowRight size={14} strokeWidth={2} />
              </Link>
            </div>
          ) : (
            <>
              <h1
                data-testid="reset-title"
                className="text-[36px] sm:text-[42px] leading-[1.05] font-semibold tracking-tight text-[#111111]"
              >
                Nouveau mot de passe
              </h1>
              <p className="mt-3 text-[15px] text-[#6B7280] leading-relaxed">
                Choisissez un mot de passe d&apos;au moins {MIN_LEN} caractères.
                Utilisez une combinaison unique et sécurisée.
              </p>

              <form
                onSubmit={handleSubmit}
                className="mt-10 space-y-4"
                data-testid="reset-form"
              >
                <div>
                  <label className="text-[12px] font-medium text-[#6B7280]">
                    Nouveau mot de passe
                  </label>
                  <div className="mt-1.5 relative">
                    <input
                      data-testid="reset-password"
                      type={showPwd ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={MIN_LEN}
                      className="w-full h-12 rounded-2xl bg-white border border-[#EEF2F5] px-4 pr-11 text-[14px] text-[#111111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111111] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      data-testid="reset-toggle-password"
                      className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-[#9CA3AF] hover:text-[#111111]"
                      aria-label="Afficher/masquer le mot de passe"
                    >
                      {showPwd ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                    </button>
                  </div>
                  {tooShort && (
                    <p className="mt-1.5 text-[11.5px] text-[#B45309]">
                      {MIN_LEN - password.length} caractère(s) restant(s)
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-[12px] font-medium text-[#6B7280]">
                    Confirmer le mot de passe
                  </label>
                  <input
                    data-testid="reset-confirm"
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="mt-1.5 w-full h-12 rounded-2xl bg-white border border-[#EEF2F5] px-4 text-[14px] text-[#111111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111111] transition-colors"
                  />
                  {mismatch && (
                    <p className="mt-1.5 text-[11.5px] text-[#991B1B]">
                      Les deux mots de passe ne correspondent pas.
                    </p>
                  )}
                </div>

                <button
                  data-testid="reset-submit"
                  type="submit"
                  disabled={!canSubmit}
                  className="mt-6 w-full h-12 rounded-full bg-[#111111] text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-black active:scale-[0.99] transition-all shadow-[0_10px_30px_-10px_rgba(17,17,17,0.4)] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? "Mise à jour…" : "Définir le mot de passe"}
                  {!loading && <ArrowRight size={15} strokeWidth={2.25} />}
                </button>

                {error && (
                  <div
                    data-testid="reset-error"
                    className="mt-3 flex items-start gap-2 rounded-xl bg-[#FEE2E2] px-3.5 py-2.5 text-[12.5px] text-[#991B1B]"
                  >
                    <AlertCircle size={13} strokeWidth={2} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </form>

              <Link
                to="/login"
                data-testid="reset-back-to-login"
                className="mt-8 inline-flex items-center gap-2 text-[13px] font-medium text-[#111111] hover:opacity-70 transition-opacity"
              >
                <ArrowLeft size={14} strokeWidth={2} />
                Retour à la connexion
              </Link>
            </>
          )}
        </div>
      </main>

      <footer className="relative z-10 px-6 pb-6">
        <p className="max-w-md mx-auto text-[11px] text-[#9CA3AF] text-center">
          © 2026 wyvio · Un espace collaboratif pour vos ventes automobiles.
        </p>
      </footer>
    </div>
  );
};

export default ResetPassword;
